import { useEffect } from 'react';
import { connect } from 'redux-bundler-react';
import { Alert } from '@trussworks/react-uswds';
import StepHeader from '@components/step-header/StepHeader';
import ExternalLink from '@components/external-link/ExternalLink';
import LinkButton from '@components/link/linkButton';
import { FileTypes, ResourceFiles } from '@src/utils/enums';
import { useRequest } from '@src/stores/request.store';

export interface NonPcnFormInstructionsProps {
  stepNo: number;
  doDownloadFile: (file: ResourceFiles, fileType: FileTypes) => void;
}

export const NonPcnFormInstructionsMetadata: {
  header: string;
  sectionName: string;
} = {
  header: 'Request Non-Preconstruction Notification',
  sectionName: 'Instructions',
};

const NonPcnFormInstructions = connect('doDownloadFile', ({ doDownloadFile, stepNo }: NonPcnFormInstructionsProps) => {
  const updateSection = useRequest((s) => s.updateSection);

  useEffect(() => {
    updateSection(stepNo, { valid: true, label: NonPcnFormInstructionsMetadata.sectionName });
  }, [updateSection, stepNo]);

  return (
    <>
      <StepHeader text='Instructions' subtext={undefined} children={undefined} />
      <p>
        Select this request type to self-verify that a preconstruction notification is not required for a proposed
        activity covered under a General Permit. This section will guide you through the process to ensure all
        information is submitted.
      </p>
      <h5 className='margin-top-3'>General Permits (GPs):</h5>
      <p>
        There are three types of general permits: Nationwide Permits (NWPs), Regional General Permits (RGPs), and
        Programmatic General Permits (PGPs). General permits are typically not tailored for individual
        applicants/projects but instead authorize activities that the USACE has determined to be substantially similar
        in nature (such as utility lines, residential developments, bank stabilization, etc.) and which cause minimal
        individual and cumulative environmental impacts. General permits can authorize activities within a limited
        geographic area (such as a county or state), a specific region (such as a group of contiguous states), or the
        entire nation.
      </p>
      <p className='text-bold'>
        Examples of activities that typically don’t require preconstruction notification include the following:
        maintenance activities, minor discharge, informational signs, scientific measuring devices, survey activities,
        some bank stabilization, temporary structures, and some boat ramps. Please review all Nationwide Permit, General
        and Regional Conditions to verify your project does not require a PCN.
      </p>
      <p className='text-bold'>
        If using a Nationwide Permit 3b, 7, 8, 17, 21, 27, 29, 31, 34, 37, 38, 39, 40, 42, 44, 45, 46, 49, 50, 52, 53,
        54, 55, or 59 for your activity, a PCN is required. To review a list of Nationwide Permits, please visit{' '}
        <ExternalLink
          href='https://www.usace.army.mil/Missions/Civil-Works/Regulatory-Program-and-Permits/Nationwide-Permits'
          content='Nationwide Permit Information'
          title='Nationwide Permit Information'
        />{' '}
      </p>
      <Alert heading='IMPORTANT' headingLevel='h5' type='warning' noIcon>
        If you're unfamiliar with the Corps Regulatory program and/or local USACE District Regulatory procedures, we
        recommend that you request a pre-application meeting with the local USACE District prior to submitting this
        request. You may also contact your local USACE District to ask general questions and to determine how to
        proceed. Visit the{' '}
        <ExternalLink
          href='https://regulatory.ops.usace.army.mil/offices/'
          title='USACE District Contact Information'
          content='USACE District Contact Information page'
        />{' '}
        for contact details.
      </Alert>
      <div className='margin-top-2'>
        <LinkButton
          onClick={() => doDownloadFile(ResourceFiles.Reg101FreqUsedTerms, FileTypes.Resource)}
          title='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
          content='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
        />
      </div>
    </>
  );
});

NonPcnFormInstructions.metadata = NonPcnFormInstructionsMetadata;

export default NonPcnFormInstructions;
